import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';

// ---------------------------------------------------------------------------
// Every webhook Unipile sends us, written down before it is interpreted
//
// This exists because we do not know the exact shape of a V2 `relation.new`
// payload. The V1 shape is documented and the V2 event NAME is documented, but
// the V2 migration note says payloads became "lighter" — "only their IDs are
// included" — and the V2 page on accepted invitations does not show the body.
//
// Guessing the field names is exactly the mistake this project has already made
// three times with account vocabulary, most recently `GOOGLE_OAUTH` becoming
// `google`. So the first real event is recorded raw, in full, and the matching
// logic reads whatever is actually there. A payload we could not interpret is
// kept with `matched: false` rather than dropped — it is the recording that
// tells us what to write next.
// ---------------------------------------------------------------------------

@Entity()
export class WebhookEvent extends MongoEntity {
  static override _class = 'WebhookEvent';
  static override _collectionName = 'webhook_events';

  /** Unipile's event type, e.g. `relation.new`. Absent if the body had none. */
  @Field(type => String) eventType?: string;

  /** The account the event is about, as Unipile named it (a V2 `acc_…` id). */
  @Field(type => String) providerAccountId?: string;

  /** Our own sending account id, once resolved. Absent when we could not map it. */
  @Field(type => String) sendingAccountId?: string;

  /** The lead this event was matched to, if any. */
  @Field(type => String) leadId?: string;

  /** Whether we understood the payload well enough to act on it. */
  @Field(type => Boolean) matched!: boolean;

  /**
   * Why not, in words. Never blank when `matched` is false: an unexplained
   * unmatched event is indistinguishable from one nobody looked at.
   */
  @Field(type => String) unmatchedReason?: string;

  /** The body exactly as it arrived, before any parsing. */
  @Field(type => String) rawBody!: string;

  /**
   * JSON: every header NAME that arrived, plus the values of the ones about
   * this integration (a value equal to the configured secret is redacted).
   *
   * Kept because a real `message.new` on 14 August carried no event type and no
   * account id in its body at all, while the dashboard displayed both — so at
   * least some of what identifies a delivery travels in the headers, and
   * guessing which is the mistake this whole module was built to avoid.
   */
  @Field(type => String) headers?: string;

  /**
   * Whether the `unipile-signature` header verified.
   *
   * A `false` here is a REFUSED delivery, kept deliberately: a 401 that leaves
   * no trace is undiagnosable by anyone without log access, which is everyone
   * on this project. Refused records are capped, because writing one needs no
   * authentication.
   */
  @Field(type => Boolean) signatureValid!: boolean;

  @Field(type => String) receivedAt!: string;

  /**
   * A stable identity for the EVENT rather than this delivery, and the unique
   * index that makes exactly one copy of it interpretable.
   *
   * Production sends every delivery three times. The copies carry different
   * envelope ids, and for messages different `account_id`s, so only the subject
   * and the moment it happened identify them as one thing — see
   * `helpers/webhook-dedupe.ts`. The uniqueness is enforced by the database
   * because two app instances make "check then insert" a race, and a lost race
   * here means a lead answered once and stopped three times.
   *
   * ABSENT on a duplicate, and absent when the payload did not carry enough to
   * build one. Both are recorded in full anyway: this collection is the log
   * that told us the triplication existed at all.
   */
  @Field(type => String) dedupeKey?: string;

  /** The delivery this one repeats, when it is a repeat. */
  @Field(type => String) duplicateOf?: string;

  /**
   * Set when the receiver could not read a field it needed — and NOT set merely
   * because the delivery was not about one of our leads.
   *
   * Two things hang off this one flag, which is why it exists rather than the
   * `unmatchedReason` prose being pattern-matched:
   *
   *   1. `GET /api/webhooks/recent` returns `rawBody` for these records. Several
   *      unmatched branches tell the reader in so many words to "read the
   *      rawBody on this record and add the real field" — that instruction is
   *      the only way the V2 payload shapes have ever been learned, and it has
   *      to keep working while ordinary mail stops being served out.
   *   2. Retention never deletes them. A bounce that arrived and could not be
   *      attributed is the first one this deployment has ever seen; ageing it
   *      out unread would repeat exactly the failure `email-bounce.ts` was
   *      written to undo.
   *
   * Absent, not `false`, on the ordinary path — the ORM writes absent optionals
   * as null, so every query on it must be `{ $ne: true }`.
   */
  @Field(type => Boolean) payloadUnreadable?: boolean;

  /**
   * A `message.new` that came FROM somebody (`is_sender: false`) — a person
   * answering on LinkedIn — whether or not it matched a lead.
   *
   * Set so an inbound DM that matched nothing is kept as evidence rather than
   * aged out as noise. That record is the only proof a reply was missed, and
   * the only thing a backfill can replay once matching is fixed: Markus'
   * replies in October were unmatched, classed as noise, and deleted after 30
   * days. Absent, not `false`, on every other path — query it as `{ $ne: true }`.
   */
  @Field(type => Boolean) inboundReply?: boolean;

  /** Set once the event has been acted on, so a replay cannot double-apply. */
  @Field(type => String) processedAt?: string;

  @Field(type => Int) processingErrors?: number;
}
